import { Button, Select } from 'antd';
import {
  DoubleLeftOutlined,
  LeftOutlined,
  RightOutlined,
  DoubleRightOutlined
} from '@ant-design/icons';

// Custom pagination footer for ErpDataGrid.
//
// AntD's built-in Pagination component renders numbered page buttons
// and lacks first/last page jumps without a quick-jumper. The Stage
// 6A spec calls for icon-only navigation (|◀ ◀ ▶ ▶|) with no page
// numbers — closer to how enterprise grids (Linear, Stripe, Excel,
// pgAdmin) handle large datasets where jumping to "page 47" is rare
// and the more useful gestures are next/prev and first/last.
//
// Layout: total text (left) · page-size select (center) · 4 nav
// icons (right). Sticks to the bottom of the grid via the parent
// `.erp-grid` flex chain (margin-top:auto and position:sticky).

export default function ErpDataGridPagination({
  current = 1,
  pageSize = 15,
  total = 0,
  pageSizeOptions = ['15', '25', '50', '100'],
  onChange
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const safeCurrent = Math.min(Math.max(1, current), lastPage);
  const rangeStart = total === 0 ? 0 : (safeCurrent - 1) * pageSize + 1;
  const rangeEnd = Math.min(safeCurrent * pageSize, total);

  const disablePrev = safeCurrent <= 1 || total === 0;
  const disableNext = safeCurrent >= lastPage || total === 0;

  const fire = (page, size) => {
    if (typeof onChange === 'function') onChange(page, size);
  };

  return (
    <div
      className="erp-grid__pagination"
      role="navigation"
      aria-label="Table pagination"
    >
      <span className="erp-grid__pagination-total">
        Showing{' '}
        <span className="erp-grid__pagination-num">
          {rangeStart.toLocaleString('en-IN')}–{rangeEnd.toLocaleString('en-IN')}
        </span>{' '}
        of{' '}
        <span className="erp-grid__pagination-num">
          {total.toLocaleString('en-IN')}
        </span>{' '}
        {total === 1 ? 'record' : 'records'}
      </span>

      <Select
        size="small"
        value={String(pageSize)}
        options={(pageSizeOptions || []).map((v) => ({
          value: String(v),
          label: `${v} / page`
        }))}
        onChange={(v) => fire(1, Number(v))}
        className="erp-grid__pagination-size"
        popupMatchSelectWidth={false}
        aria-label="Page size"
      />

      <div className="erp-grid__pagination-nav">
        <Button
          size="small"
          icon={<DoubleLeftOutlined />}
          aria-label="First page"
          disabled={disablePrev}
          onClick={() => fire(1, pageSize)}
          className="erp-grid__pagination-btn"
        />
        <Button
          size="small"
          icon={<LeftOutlined />}
          aria-label="Previous page"
          disabled={disablePrev}
          onClick={() => fire(safeCurrent - 1, pageSize)}
          className="erp-grid__pagination-btn"
        />
        <Button
          size="small"
          icon={<RightOutlined />}
          aria-label="Next page"
          disabled={disableNext}
          onClick={() => fire(safeCurrent + 1, pageSize)}
          className="erp-grid__pagination-btn"
        />
        <Button
          size="small"
          icon={<DoubleRightOutlined />}
          aria-label="Last page"
          disabled={disableNext}
          onClick={() => fire(lastPage, pageSize)}
          className="erp-grid__pagination-btn"
        />
      </div>
    </div>
  );
}
